import React from "react";

import {
  TextField,
  Button,
  Alert,
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";

const ModernTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "rgba(248, 250, 252, 0.8)",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      backgroundColor: "rgba(248, 250, 252, 1)",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(25, 118, 210, 0.3)",
      },
    },
    "&.Mui-focused": {
      backgroundColor: "rgba(255, 255, 255, 1)",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#1976d2",
        borderWidth: 2,
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(0, 0, 0, 0.12)",
      transition: "border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    },
  },
  "& .MuiInputLabel-root": {
    fontWeight: 500,
    color: "#374151",
    "&.Mui-focused": {
      color: "#1976d2",
    },
  },
  "& .MuiFormHelperText-root": {
    marginLeft: 4,
    fontSize: "0.875rem",
    fontWeight: 500,
    "&.Mui-error": {
      color: "#ef4444",
    },
  },
}));

const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.95rem",
  padding: theme.spacing(1.5, 3),
  minHeight: 48,
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  "&.MuiButton-contained": {
    background: "linear-gradient(135deg, #1976d2, #1565c0)",
    boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
    "&:hover": {
      background: "linear-gradient(135deg, #1565c0, #0d47a1)",
      boxShadow: "0 6px 20px rgba(25, 118, 210, 0.4)",
      transform: "translateY(-1px)",
    },
    "&:disabled": {
      background: "rgba(156, 163, 175, 0.5)",
      color: "rgba(255, 255, 255, 0.7)",
      boxShadow: "none",
      transform: "none",
    },
  },
  "&.MuiButton-outlined": {
    borderColor: "rgba(0, 0, 0, 0.12)",
    color: "#6b7280",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
      borderColor: "rgba(0, 0, 0, 0.2)",
    },
  },
}));

const ModernAlert = styled(Alert)(({ theme }) => ({
  borderRadius: 12,
  marginBottom: theme.spacing(2),
  fontWeight: 500,
  "& .MuiAlert-icon": {
    alignItems: "center",
  },
  "&.MuiAlert-standardError": {
    backgroundColor: "rgba(254, 242, 242, 0.8)",
    borderLeft: "4px solid #ef4444",
    color: "#991b1b",
  },
  "&.MuiAlert-standardSuccess": {
    backgroundColor: "rgba(236, 253, 245, 0.8)",
    borderLeft: "4px solid #1976d2",
    color: "#065f46",
  },
  "&.MuiAlert-standardWarning": {
    backgroundColor: "rgba(255, 251, 235, 0.8)",
    borderLeft: "4px solid #f59e0b",
    color: "#92400e",
  },
  "&.MuiAlert-standardInfo": {
    backgroundColor: "rgba(239, 246, 255, 0.8)",
    borderLeft: "4px solid #3b82f6",
    color: "#1e40af",
  },
}));

const ModernFormControl = styled(FormControl)(({ theme }) => ({
  "& .MuiFormLabel-root": {
    fontWeight: 600,
    color: "#374151",
    fontSize: "1rem",
    marginBottom: theme.spacing(1),
    "&.Mui-focused": {
      color: "#1976d2",
    },
  },
}));

const ModernRadioGroup = styled(RadioGroup)(({ theme }) => ({
  marginTop: theme.spacing(1),
  "& .MuiFormControlLabel-root": {
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: 12,
    border: "1px solid rgba(0, 0, 0, 0.08)",
    backgroundColor: "rgba(248, 250, 252, 0.5)",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      backgroundColor: "rgba(248, 250, 252, 1)",
      borderColor: "rgba(25, 118, 210, 0.2)",
    },
    "& .MuiRadio-root": {
      color: "#9ca3af",
      "&.Mui-checked": {
        color: "#1976d2",
      },
    },
  },
}));

const LoadingButton = styled(ModernButton)(({ theme }) => ({
  "& .MuiCircularProgress-root": {
    color: "rgba(255, 255, 255, 0.8)",
    marginRight: theme.spacing(1),
  },
}));

interface ModernFormFieldProps {
  label: string;
  name: string;
  type?: string;
  multiline?: boolean;
  rows?: number;
  error?: boolean;
  helperText?: string;
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  fullWidth?: boolean;
  required?: boolean;
}

export function ModernFormField({
  label,
  name,
  type = "text",
  multiline = false,
  rows = 1,
  error = false,
  helperText,
  value,
  onChange,
  onBlur,
  placeholder,
  fullWidth = true,
  required = false,
}: ModernFormFieldProps) {
  return (
    <ModernTextField
      fullWidth={fullWidth}
      margin="normal"
      label={label}
      name={name}
      type={type}
      multiline={multiline}
      rows={multiline ? rows : undefined}
      error={error}
      helperText={helperText}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      required={required}
      variant="outlined"
    />
  );
}

interface ModernSubmitButtonProps {
  label: string;
  loading?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  type?: "button" | "submit";
  variant?: "contained" | "outlined";
  fullWidth?: boolean;
}

export function ModernSubmitButton({
  label,
  loading = false,
  disabled = false,
  onClick,
  type = "submit",
  variant = "contained",
  fullWidth = false,
}: ModernSubmitButtonProps) {
  return (
    <LoadingButton
      type={type}
      variant={variant}
      disabled={disabled || loading}
      onClick={onClick}
      fullWidth={fullWidth}
      sx={{ mt: 2, mb: 1 }}
    >
      {loading && <CircularProgress size={20} />}
      {label}
    </LoadingButton>
  );
}

interface ModernAlertProps {
  severity: "error" | "warning" | "info" | "success";
  children: React.ReactNode;
}

export function ModernFormAlert({ severity, children }: ModernAlertProps) {
  return <ModernAlert severity={severity}>{children}</ModernAlert>;
}

interface ModernRadioFieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  options: Array<{
    value: string;
    label: string;
    description?: string;
  }>;
  error?: boolean;
  helperText?: string;
}

export function ModernRadioField({
  label,
  name,
  value,
  onChange,
  options,
  error = false,
  helperText,
}: ModernRadioFieldProps) {
  return (
    <ModernFormControl error={error} sx={{ mt: 2 }}>
      <FormLabel>{label}</FormLabel>
      <ModernRadioGroup value={value} onChange={onChange} name={name}>
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio />}
            label={
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {option.label}
                </Typography>
                {option.description && (
                  <Typography variant="body2" color="text.secondary">
                    {option.description}
                  </Typography>
                )}
              </Box>
            }
          />
        ))}
      </ModernRadioGroup>
      {helperText && (
        <Typography variant="caption" color="error">
          {helperText}
        </Typography>
      )}
    </ModernFormControl>
  );
}

export { ModernTextField, ModernButton, ModernAlert };
